// Every tunable scalar/numeric game-balance constant lives here, grouped by the domain that consumes it.
// This is NOT the place for: signals/state, debug-tool
// definitions, icon/UI maps, string identifiers that reference content by name/type (except the core
// currency/reagent material names under Item), or fixed constants
// dictated by an external format/protocol (e.g. the Tiled map format's GID bitmask flags, IndexedDB
// connection details) rather than authored game balance

import type {
  BaseStat,
  CombatStat,
  DebuffPotency,
  GameElement,
  MonsterType,
  StatusEffectTag,
} from '@interfaces';

// Caravan

export const ACTIVE_TRADE_COUNT = 4;
export const URGENCY_SAFE_MIN_TICKS = 1800; // 30 minutes
export const URGENCY_WARNING_MIN_TICKS = 300; // 5 minutes

// Combat

export const COMBAT_ORDER_ROW_CAP = 10;
export const SKILL_MAX_ALLY_TARGETS = 4;
export const COMBAT_ELEMENT_CHARGES_PER_ELEMENT = 2;

// Resistance can't reach immunity; a weakness can at most double incoming damage.
export const ELEMENT_RESISTANCE_MAX = 75;
export const ELEMENT_RESISTANCE_MIN = -100;

// XP degrades once the party out-levels a node's max, then drops to 0 at the hard cap - keeps overleveled parties from farming trivial nodes.
export const OVERLEVEL_XP_DEGRADE_PER_LEVEL = 0.25;
export const OVERLEVEL_XP_DEGRADE_FLOOR = 1;
export const OVERLEVEL_XP_HARD_CAP_LEVELS = 4;

// Commission

// Fixed UTC-6 offset year-round (no DST handling, by design) - "3AM CST" as a wall-clock reset boundary.
// See CaravanContent.traderResetTime. See CommissionNodeState.generatedAt.
export const COMMISSION_RESET_HOUR_UTC = 9;
export const ONE_DAY_MS = 24 * 60 * 60 * 1000;

// Crafting

export const MAX_CRAFTABLE_CAP = 99;
export const TRADESKILL_MAX_LEVEL = 50;
export const TRADESKILL_XP_START = 10;
export const TRADESKILL_XP_END = 5000;
export const TRADESKILL_QUEUE_BASE_SIZE = 2;
export const TRADESKILL_QUEUE_LEVELS_PER_SLOT = 5;
export const TRADESKILL_QUEUE_MAX_SIZE = 16;

// Decree

// Losing streak at which mostChallengingExploreNodeForRisk gives up on a tier and steps down.
export const LEVEL_UP_NODE_FAILURE_LIMIT = 5;

// Edict

// Each Fragment moves a run's score multiplier by this much; boons can't push it below the floor.
export const EDICT_FRAGMENT_MULTIPLIER_STEP = 0.1;
export const EDICT_SCORE_MULTIPLIER_FLOOR = 0.25;

// Engine

export const GAMELOOP_INTERVAL_MS = 1000;
export const MAX_EVENT_SEGMENTS = 5;
export const MAX_SEGMENT_LENGTH = 32;
// How long the loading screen lingers once the page beneath it has rendered; covers the bar's 400ms fill.
export const LOADING_FULL_HOLD_MS = 900;
// Cap on waiting for the page rendered under the loading screen to settle before the hold starts.
export const LOADING_IDLE_TIMEOUT_MS = 3000;
// Logs which loading steps are still pending, for "stuck on the loading screen" reports.
export const LOADING_STALL_WARN_MS = 30000;
// Per-entry cap on what's sent to the desktop log file; debug state dumps can run to megabytes.
export const LOG_FILE_MAX_ENTRY_LENGTH = 20000;
// Average busy time per tick (yields excluded) past which a gameloop batch is logged as slow; ~3 frames.
export const GAMELOOP_SLOW_TICK_WARN_MS = 50;
// Slow batches in between are counted rather than logged, so a struggling machine doesn't flood the log.
export const GAMELOOP_SLOW_TICK_WARN_COOLDOWN_MS = 60000;
// Images only stylesheets reference; decoded during loading so nothing paints untextured after it. Paths match the CSS exactly.
export const LOADING_PRELOAD_IMAGES = [
  '/asset/texture-bg.png',
  '/asset/texture-button.png',
  '/asset/splash-game.png',
  '/asset/splash-dark.png',
];
// One CSS font shorthand per @font-face in font.scss, so each face is fetched during loading.
export const LOADING_PRELOAD_FONTS = [
  '1em FancyText',
  '400 1em OtherText',
  '400 1em NumericText',
  '400 1em CleanText',
  'italic 400 1em CleanText',
];
export const LOADING_BACKDROP_CELL_PX = 72;
export const LOADING_BACKDROP_SWAP_MS = 400;
export const LOADING_HERO_CYCLE_MS = 2500;
// The resolution the UI was laid out at; auto UI scale only grows past it.
export const UI_SCALE_BASE_WIDTH = 1920;
export const UI_SCALE_BASE_HEIGHT = 1080;
export const UI_SCALE_AUTO_MAX = 2;
// A catch-up batch or several heroes leveling at once should play one sound, not a stack.
export const GAME_SFX_MIN_INTERVAL_MS = 1000;

// Savefile

export const SAVEFILE_BACKUP_MAX = 3;
export const SAVEFILE_READ_ATTEMPTS = 3;
export const SAVEFILE_MIGRATE_ATTEMPTS = 3;
export const SAVEFILE_RETRY_DELAY_MS = 250;
export const SAVEFILE_SAVE_ERROR_NOTIFY_INTERVAL_MS = 60_000;

// Beyond this many levels above the party's floor, a range is excluded outright (TooHigh) regardless of risk setting.
export const HIGH_RISK_LEVELS_ABOVE_PARTY = 7;

// Caps how many ticks run before yielding to the browser during a long catch-up (e.g. after the tab was backgrounded), so it doesn't block the main thread for seconds at a time.
export const TICKS_PER_YIELD = 100;

export const ADVENTURE_LOG_OVERLAY_MAX_LINES = 15;
// Ticks, not wall-clock time, so lines don't expire while the game is paused.
export const ADVENTURE_LOG_OVERLAY_LIFETIME_TICKS = 20;

// Rows the Adventure Log page renders up front and adds per scroll-to-bottom; rendering all 500 made opening it sluggish.
export const ADVENTURE_LOG_PAGE_SIZE = 50;
// Distance from the bottom of the Adventure Log at which the next page renders, so it's in place before the user reaches it.
export const ADVENTURE_LOG_LOAD_MORE_THRESHOLD_PX = 600;

// A "practically forever" duration for a global effect that's removed explicitly (on a state change or leaving a map), not by natural expiry - long enough it never lapses on its own during normal play. Used by the Idle and Auto Mode effects.
export const ONE_YEAR_TICKS = 60 * 60 * 24 * 365;

// Hero

export const HEALING_MINIMUM_SECONDS = 10;
export const HEALING_SECONDS_PER_LEVEL = 2;
export const RECLASS_GOLD_PER_LEVEL = 100;
export const CHARACTER_MAX_LEVEL = 99;
export const PARTY_SIZE_MAX = 4;
export const HERO_BURST_SKILL_SLOTS = 1;
export const REGALIA_MAX_PER_JOB = 3;
export const REGALIA_NAME_MAX_LENGTH = 20;
export const CHARACTER_XP_START = 100;
export const CHARACTER_XP_END = 100000;
export const RESTING_REGEN_PERCENT = 0.01;
export const TICKS_PER_STEP_ON_PATH = 1;
export const TICKS_PER_STEP_OFF_PATH = 3;
export const TICKS_PER_STEP_MIN_DIFF = 0.25;
export const TRAVEL_UNITS_PER_TICK = 100;
export const DEATHS_DOOR_SECONDS_PER_MAP = 10;
export const DEATHS_DOOR_MINIMUM_SECONDS = 10;
// Minimum ticks between two Intimidation auto-wins, so Intimidation can't instantly sweep a chain of weak nodes.
export const INTIMIDATION_COOLDOWN_TICKS = 15;
// Shared by every shrine, since a prayer anywhere replaces the last shrine's buff.
export const SHRINE_PRAYER_COOLDOWN_TICKS = 300;
// Per home-outpost level past the build (+1), applied to both Deaths Door and Healing.
export const OUTPOST_DEATH_PENALTY_REDUCTION_PER_LEVEL = 0.25;
// Levels past this add no further reduction, so the teleport level can't zero out Deaths Door.
export const OUTPOST_DEATH_PENALTY_MAX_LEVEL = 4;
// Build (+1), three death-penalty upgrades, then the teleport unlock; enforced on content by validate:outposts.
export const OUTPOST_MAX_LEVEL = 5;
export const OUTPOST_TELEPORT_LEVEL = 5;

// Shared exponent for the house XP-curve shape (start + (end - start) * progress^EASE) used by character,
// tradeskill, and worker leveling - see patterns.md's "shared XP-curve shape" note.
export const XP_CURVE_EASE = 1.5;

// Item

export const GOLD_COIN_NAME = 'Gold Coin';
export const CRIMSON_LUCRE_NAME = 'Crimson Lucre';
export const TRADER_TOKEN_NAME = 'Trader Scrip';
export const MEMORY_FLUX_NAME = 'Memory Flux';
export const REFORGE_REAGENT_NAME = 'Duskhall Flux';

export const GOLD_PER_STAT_POINT = 30;
export const GOLD_PER_RESISTANCE_POINT = 100;
export const GOLD_PER_COMBAT_STAT_POINT = 50;
export const STARTING_GOLD_AMOUNT = 100;

// a rarer stat (Luck) costs/sells for more per point than a common one (Health).
export const VALUE_MULTIPLIER_PER_STAT: Record<BaseStat, number> = {
  Agility: 2,
  Constitution: 3,
  Energy: 1,
  Health: 1,
  Intelligence: 5,
  Luck: 10,
  Resistance: 4,
  Spirit: 3,
  Strength: 5,
  Vitality: 4,
};

export const VALUE_MULTIPLIER_PER_RESISTANCE: Record<StatusEffectTag, number> =
  {
    Stun: 6,
    StatDown: 7,
    Accuracy: 3,
    DamageOverTime: 10,
    Poison: 3,
    Burn: 5,
    Bleed: 3,
  };

// Skill trees

// Every job has exactly this many default nodes, so a new hero starts fully allocated.
export const SKILL_TREE_STARTING_SP = 2;
// One SP every this many levels, up to the last column's level.
export const SKILL_TREE_SP_LEVEL_INTERVAL = 2;
export const SKILL_TREE_COLUMN_INTERVAL = 5;
export const SKILL_TREE_MAX_COLUMN_LEVEL = 50;
// A tree draws at most this many equal-width base columns, so its width stays predictable.
export const SKILL_TREE_MAX_BASE_COLUMNS = 3;
export const SKILL_TREE_SPEC_COLUMNS = 2;
export const SKILL_TREE_MAX_TRACKS = 3;
export const SKILL_TREE_SPEC_OATH_LEVEL = 15;

// Skill tree layout, in pixels.
export const SKILL_TREE_NODE_SIZE = 48;
export const SKILL_TREE_LEVEL_LABEL_WIDTH = 56;
export const SKILL_TREE_GROUP_TITLE_HEIGHT = 20;
export const SKILL_TREE_LANE_TITLE_HEIGHT = 32;
export const SKILL_TREE_ROW_HEIGHT = 84;
export const SKILL_TREE_COLUMN_GAP = 16;
// Keeps an oath frame clear of the arrows on neighbouring tracks; narrower trees scroll instead.
export const SKILL_TREE_TRACK_MIN_WIDTH = 64;
export const SKILL_TREE_OATH_PADDING = 4;
export const SKILL_TREE_OATH_BORDER = 2;
export const SKILL_TREE_OATH_OPTION_GAP = 8;

// Monster Research: what each rank of the DebuffPotency job ability adds.
export const DEBUFF_POTENCY_PER_RANK: DebuffPotency = {
  chance: 5,
  duration: 1,
};

export const VALUE_MULTIPLIER_PER_COMBAT_STAT: Record<CombatStat, number> = {
  repeatActionChance: 5,
  skillStrikeAgainChance: 6,
  redirectionChance: 1,
  missChance: 1,
  debuffIgnoreChance: 7,
  damageReflectPercent: 3,
  healingIgnorePercent: 1,
  reviveChance: 10,
  stunChance: 1,
  epCostIncreasePercent: 1,
  agroValue: 2,
  coverThreshold: 6,
};

export const GOLD_PER_MONSTER_TYPE_DAMAGE_POINT = 50;
export const VALUE_MULTIPLIER_PER_MONSTER_TYPE: Record<MonsterType, number> = {
  Humanoid: 3,
  Demon: 10,
  Amalgamation: 11,
  Insect: 7,
  Beast: 5,
  Spirit: 15,
  Forgetter: 20,
};

export const GOLD_PER_ELEMENT_RESIST_POINT = 50;
export const GOLD_PER_ELEMENT_BOON_POINT = 50;
export const VALUE_MULTIPLIER_PER_ELEMENT: Record<GameElement, number> = {
  Fire: 5,
  Water: 5,
  Earth: 5,
  Air: 5,
};

export const GOLD_PER_GATHER_YIELD_POINT = 200;

// Per 1.0x of extra scaling, weighted per boosted stat.
export const GOLD_PER_SKILL_STAT_BONUS_POINT = 250;

// Kingdom

export const ARMORY_CAP = 50;
// Drop hard-stop AND the "Over Capacity" warning tier threshold - same ratio, one constant.
export const ARMORY_OVERFLOW_MULTIPLIER = 1.25;
// "Overburdened" tier is just ratio >= 1 (no separate constant needed).
export const ARMORY_ENCUMBERED_THRESHOLD = 0.75;

// Same rate infusion pricing uses, plus a per-level component so higher-tier drops are worth more.
export const SELL_GOLD_PER_STAT_POINT = 5;
export const SELL_GOLD_PER_LEVEL = 2;

// Combat stats are rarer/more specialized than a raw base stat point, so they're worth more.
export const SELL_GOLD_PER_COMBAT_STAT_POINT = 12;

// Same rate infusion pricing uses for a resistance point - the rarest, most specialized bonus.
export const SELL_GOLD_PER_RESISTANCE_POINT = 20;

export const REFORGE_GOLD_PER_LEVEL = 25;

// Only one spell can be active for now - raising this later is a one-line change.
export const MAX_ACTIVE_ASTRAL_PROJECTOR_SPELLS = 1;

// Loot filter: a dropped item that fails the player's filters is auto-sold
// for this fraction of its normal sell value instead of entering the armory.
export const LOOT_FILTER_AUTO_SELL_PERCENT = 0.8;
export const LOOT_FILTER_DEFAULT_MIN_ITEM_LEVEL = 1;

// Decree

export const DECREE_CLAUSE_CAP = 7;

// How often an in-progress clause checks whether a higher-priority one has become actionable.
export const DECREE_PRIORITY_RECHECK_INTERVAL_TICKS = 5;

// Pathfinding

// Path tiles are cheaper than other open tiles so pathfinding hugs authored paths without blocking off-road.
export const ON_PATH_MOVE_COST = 1;
export const OFF_PATH_MOVE_COST = 4;
// Dwarfs any real walk, so a pass-through route only crosses a node when no route around it exists.
export const NODE_PASS_THROUGH_MOVE_COST = 100_000;

// Pixi

export const GATHER_VFX_LIFETIME_MS = 1100;
export const GATHER_VFX_FLOAT_DISTANCE = 40;
export const GATHER_VFX_JITTER_RANGE = 30;
// Quotes (e.g. a rescued worker's thanks) stay fully opaque for most of their life so they can be read.
export const GATHER_VFX_QUOTE_LIFETIME_MS = 6000;
export const GATHER_VFX_QUOTE_HOLD_FRACTION = 0.75;
export const GATHER_VFX_QUOTE_WRAP_WIDTH = 220;
export const NODE_REVEAL_LIFETIME_MS = 900;
export const NODE_REVEAL_POP_MS = 450;
// easeOutBack's overshoot; ~1.7 peaks about 10% past full size before settling.
export const NODE_REVEAL_OVERSHOOT = 1.70158;
export const NODE_REVEAL_RING_RADIUS_TILES = 1.5;
export const INDICATOR_PROGRESS_BAR_HEIGHT = 6;
export const INDICATOR_PROGRESS_BAR_OFFSET_Y = -60;
export const NODE_STATUS_ICON_RADIUS = 7;
// Still fast enough for node timers and the 150ms status refresh to read as live.
export const MAP_UNFOCUSED_MAX_FPS = 10;
// World px per noise pixel - matches the terrain art's chunky pixel size so the overlay reads as pixel art.
export const TERRAIN_NOISE_CELL_PX = 2;
// Noise pixels per cluster-noise lattice unit; larger = broader patches of speckles.
export const TERRAIN_NOISE_FEATURE_CELLS = 4;
// Peak speckle chance per noise pixel, reached only at the densest point of a cluster.
export const TERRAIN_NOISE_DENSITY = 0.1;
export const TERRAIN_NOISE_ALPHA = 0.07;
export const BACKDROP_SHARD_COUNT = 160;
// Parallax factor range; lower = farther away, so it drifts less than the map while panning.
export const BACKDROP_SHARD_DEPTH_MIN = 0.15;
export const BACKDROP_SHARD_DEPTH_MAX = 0.6;
export const BACKDROP_SHARD_BASE_SIZE_PX = 21;
export const BACKDROP_SHARD_ALPHA_MIN = 0.55;
export const BACKDROP_SHARD_ALPHA_MAX = 0.95;
export const BACKDROP_SHARD_SHADE_MIN = 0.75;
export const BACKDROP_SHARD_SHADE_MAX = 1.1;
export const BACKDROP_SHARD_QUAD_CHANCE = 0.18;
export const BACKDROP_SHARD_HIGHLIGHT_EDGE_CHANCE = 0.28;
// Memory shards carry a fragment of node art, drawn larger so the image stays legible.
export const BACKDROP_SHARD_MEMORY_CHANCE = 0.12;
export const BACKDROP_SHARD_MEMORY_SIZE_SCALE = 2.6;
export const BACKDROP_SHARD_PERIOD_MIN_MS = 6000;
export const BACKDROP_SHARD_PERIOD_MAX_MS = 16000;
// Share of each cycle spent flaring, so only a handful of shards twinkle at once.
export const BACKDROP_SHARD_FLARE_FRACTION = 0.15;
// One full 3D turn about the shard's own in-plane axis.
export const BACKDROP_SHARD_TUMBLE_PERIOD_MIN_MS = 30000;
export const BACKDROP_SHARD_TUMBLE_PERIOD_MAX_MS = 60000;
// A share of shards turn faster than the slow drift, so some motion is always visible.
export const BACKDROP_SHARD_FAST_TUMBLE_CHANCE = 0.35;
export const BACKDROP_SHARD_FAST_TUMBLE_PERIOD_MIN_MS = 12000;
export const BACKDROP_SHARD_FAST_TUMBLE_PERIOD_MAX_MS = 20000;
// Only some shards glint when they flare, and glints are capped so big memory shards don't throw huge stars.
export const BACKDROP_SHARD_GLINT_CHANCE = 0.25;
export const BACKDROP_SHARD_GLINT_REACH_MAX_PX = 14;
// Edge-on shards dim to this, and back faces dim further, so the turn reads as lit from the front.
export const BACKDROP_SHARD_LIGHT_MIN = 0.45;
export const BACKDROP_SHARD_BACKFACE_LIGHT = 0.7;
export const BACKDROP_SHARD_SHIMMER_PERIOD_MS = 450;
export const BACKDROP_SHARD_FLOAT_SPEED_X = 0.00035;
export const BACKDROP_SHARD_FLOAT_SPEED_Y = 0.0003;
export const BACKDROP_SHARD_WOBBLE_SPEED = 0.00025;
// Field padding so shards wrap offscreen instead of popping in at the viewport edge.
export const BACKDROP_FIELD_MARGIN_PX = 60;
// Muted plums and browns echoing splash-dark.
export const BACKDROP_SHARD_COLORS: ReadonlyArray<number> = [
  0x452c3b, 0x543641, 0x674543, 0x745249, 0x5b4844, 0x815b4d, 0x8f6c53,
  0x433142, 0x6d4c54, 0x4f3e3c, 0x9b7a5d, 0x5f3a49,
];
export const BACKDROP_STAR_COUNT = 260;
export const BACKDROP_STAR_DEPTH = 0.05;
export const BACKDROP_STAR_PULSE_SPEED = 0.0005;

// How far each trailing party member's screen position lags behind the leader's, in party order (position 2 = 1x, position 3 = 2x, ...).
export const PARTY_FORMATION_FOLLOW_DELAY_MS = 450;
// How long a follower takes to converge onto the node tile once the leader visually arrives, so the party doesn't wait out its full follow delay to settle.
export const PARTY_FORMATION_CATCHUP_MS = 250;
// Leader-position sample buffer only needs to cover the longest follow delay in use, plus headroom.
export const PARTY_FORMATION_HISTORY_MAX_AGE_MS = 2400;
// Followers are placed at a randomized radius (tiles) in this range, spread evenly by angle around the
// leader - the nonzero minimum is what actually guarantees no two sprites ever land on the same spot.
export const PARTY_FORMATION_JITTER_MIN_TILES = 0.4;
export const PARTY_FORMATION_JITTER_MAX_TILES = 0.6;
// A travel token's leftover offset after a tick (redirect, timing jitter) eases out over this long instead of snapping.
export const TRAVEL_GLIDE_CORRECTION_MS = 250;
// Gaps wider than this (e.g. a background catch-up) snap rather than slide across the map.
export const TRAVEL_GLIDE_MAX_CORRECTION_TILES = 2;

// Tasks

export const TASK_WIDGET_ENTRY_COUNT = 3;

// Town

export const TOWN_REPUTATION_MAX_TIER = 4;

// No authored weight value exists yet - a specialty recipe is this many times as likely to be picked as a non-specialty one.
export const SPECIALTY_RECIPE_WEIGHT = 3;
export const TOWN_SPECIALTY_PRIORITY_TICK_INTERVAL = 1;
export const TOWN_PRIORITY_WEIGHT_PER_FAILURE = 0.5;
export const TOWN_PRIORITY_MAX_FAILURES_FOR_WEIGHT = 20;

// Past this many failures, a specialty recipe's materials are heavily deprioritized (not blocked) for everything but other specialty recipes.
export const TOWN_SPECIALTY_FAILURE_HOLD_THRESHOLD = 100;
// A deprioritized recipe is picked at roughly this fraction of its normal weight - still able to drain the pile, just rarely, so the struggling recipe gets first crack at its own materials without starving forever if it never succeeds.
export const TOWN_SPECIALTY_FAILURE_HOLD_WEIGHT_PENALTY = 0.1;

// A recipe stops being picked once this many combined copies of its result already sit in shop stock + the craft queue - keeps one recipe from flooding both.
export const TOWN_RECIPE_OUTPUT_DUPLICATE_CAP = 3;

// Uncapped - the dedicated specialty commission should keep escalating for as long as its recipe keeps failing.
export const TOWN_SPECIALTY_COMMISSION_WEIGHT_PER_FAILURE = 1;

// Runs every tick once activated, same reasoning as WORKER_TICK_INTERVAL - craft progress is continuous.
export const CRAFT_TICK_INTERVAL = 1;
// Queued up front on first visit so a newly-activated town isn't sitting idle.
export const TOWN_FIRST_VISIT_CRAFT_COUNT = 4;
// Equipment crafts completed instantly on first visit, before the queue fills, so the shop isn't empty on arrival.
export const TOWN_FIRST_VISIT_COMPLETED_CRAFT_COUNT = 4;
export const RAID_LOSS_CRAFT_DEBUFF_MULTIPLIER = 2;
export const RAID_WIN_REPUTATION_AMOUNT = 100;
export const RAID_LOSS_REPUTATION_AMOUNT = 50;
// Paying off a raid (tribute/fortify) is a lesser win than actually fighting it.
export const RAID_BUYOFF_REPUTATION_AMOUNT = 25;
export const RAID_LOSS_CRAFT_DEBUFF_TICKS = 3600;

// Raiders take 1 to (this % of the town's stock cap, not its current stock count) random stock entries.
export const RAID_LOSS_STOCK_MAX_STEAL_PERCENT = 50;

// Raiders take this % of every material stack the town is holding.
export const RAID_LOSS_MATERIAL_STEAL_PERCENT = 50;

// Re-check cadence, not the actual cap - see RAID_COOLDOWN_TICKS for that.
export const RAID_CHECK_INTERVAL_TICKS = 60;
export const RAID_COOLDOWN_TICKS = 14400;
export const RAID_BASE_WARNING_TICKS = 300;
export const RAID_WARNING_TICKS_PER_MAP_HOP = 120;
export const RAID_WARNING_TICKS_PER_REPUTATION_TIER = 60;

// Checking stock ages is cheap - run every tick so an expired entry disappears promptly, not in whatever-tick-interval batches.
export const SHOP_TICK_INTERVAL = 1;

// Same cadence as every other town subsystem (worker/craft/shop).
export const TOWN_COMMISSION_TICK_INTERVAL = 1;

// Runs every tick once activated - worker travel/gathering progress continuously.
// Raising this is a blunt slowdown on the whole worker economy - it advances per-tick progress by +1 regardless of how many real ticks elapsed since the last run.
export const WORKER_TICK_INTERVAL = 1;

// Worker pauses this long between filling up and hauling back
export const TOWN_WORKER_REST_TICKS = 30;

// Honored - the minimum reputation tier at which a town can be designated home.
export const TOWN_HOME_MIN_REPUTATION_TIER = 2;

// Worker

export const WORKER_MAX_LEVEL = 99;
export const WORKER_XP_START = 10;
export const WORKER_XP_END = 10000;
// Stamina a worker needs per tradeskill level of a recipe's minimum, to be assigned to craft it.
export const WORKER_CRAFT_STAMINA_PER_TRADESKILL_LEVEL = 5;

// Common desktop sizes at or above the Electron window's 1280x720 minimum.
export const WINDOW_SIZE_PRESETS: ReadonlyArray<{
  width: number;
  height: number;
}> = [
  { width: 1280, height: 720 },
  { width: 1366, height: 768 },
  { width: 1440, height: 900 },
  { width: 1600, height: 900 },
  { width: 1680, height: 1050 },
  { width: 1920, height: 1080 },
  { width: 1920, height: 1200 },
  { width: 2560, height: 1440 },
  { width: 2560, height: 1600 },
  { width: 3840, height: 2160 },
];
